import { SDKConfig,AuthResponse } from '../types';
import { DEFAULT_BASE_URL } from '../utils/api';


interface DecodedToken {
  exp?: number;
  userId?: string;
  [key: string]: any;
}

export class AuthManager {
  private apiKey: string;
  private baseURL: string;
  private token: string | null = null;
  private userId: string | null = null;
  private tokenExpiration: number | null = null;

  constructor(config: SDKConfig) {
    this.apiKey = config.apiKey;
    this.baseURL = config.baseURL || DEFAULT_BASE_URL;
  }

  private parseJwt(token: string): DecodedToken {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.warn('Error parsing JWT token:', error);
      return {};
    }
  }

  private setAuthData(response: AuthResponse): void {
    if (!response.results || !response.results.token) {
      throw new Error('Invalid authentication response');
    }
    
    this.token = response.results.token;
    this.userId = response.results.user_id;
    
    if (this.token) {
      const decoded = this.parseJwt(this.token);
      if (decoded.exp) {
        this.tokenExpiration = decoded.exp * 1000;
      } else {
        // Set a default expiration of 1 hour if not provided
        this.tokenExpiration = Date.now() + (60 * 60 * 1000);
      }
      
      // Debug log
      // console.log('Token set successfully:', {
      //   hasToken: !!this.token,
      //   hasUserId: !!this.userId,
      //   expiration: new Date(this.tokenExpiration || 0).toISOString()
      // });
    }
  }

  private isTokenExpired(): boolean {
    if (!this.tokenExpiration) return false;
    // Add 5-second buffer to prevent edge cases
    return Date.now() > (this.tokenExpiration - 5000);
  }

  async initialize(): Promise<boolean> {
    try {
      const result = await fetch(`${this.baseURL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey
        }
      });

      if (!result.ok) {
        if (result.status === 401 || result.status === 403) {
          this.clearAuth();
          throw new Error('Invalid API key');
        }
        throw new Error(`HTTP error! status: ${result.status}`);
      }

      const response: AuthResponse = await result.json();
      
      if (response.status === 200 && response.results?.token) {
        this.setAuthData(response);
        return true;
      }
      
      return false;
    } catch (error) {
      this.clearAuth();
      throw new Error(`Authentication failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async refreshTokenIfNeeded(): Promise<boolean> {
    if (this.isTokenExpired()) {
      return this.initialize();
    }
    return true;
  }

  async getValidToken(): Promise<string> {
    try {
      if (!this.token || this.isTokenExpired()) {
        console.log('Token needs refresh...');
        await this.initialize();
      }
      
      if (!this.token) {
        throw new Error('Unable to obtain valid token');
      }
      
      return this.token;
    } catch (error) {
      console.error('Token validation error:', error);
      throw error;
    }
  }

  getUserId(): string {
    if (!this.userId) {
      throw new Error('User ID not available');
    }
    return this.userId;
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return this.token !== null && !this.isTokenExpired();
  }

  clearAuth(): void {
    this.token = null;
    this.userId = null;
    this.tokenExpiration = null;
  }
}